import React from 'react';
import cn from 'classnames';
import gsap from 'gsap';
import Div100vh from 'react-div-100vh';
import { mergeRefs } from 'src/utils/merge-refs';
import { Scroller } from 'src/utils/scroller';

import css from './ScreenSlider.module.scss';

interface Props<T> {
  className?: string;
  items: T[];
  page: number;
  total: number;
  scroller: Scroller;
  active?: boolean;
  busy?: boolean;
  destroyOnReachingTop?: boolean;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  slide: React.FC<{ data: T | null; position?: 'prev' | 'current' | 'next'; isActive?: boolean }>;
  noResults: React.FC;
  onPageChange: (page: number) => unknown;
  onDeactivate: () => unknown;
  style?: React.CSSProperties;
}

const TRANSITION_DURATION = 0.35;
const PEEK_SIZE = 0;
const FALLBACK_SLIDE_STEP = 573;
const WINDOW_OFFSETS = [-1, 0, 1] as const;

function getSlideKey<T>(item: T, index: number) {
  const id = (item as { _id?: string | number })._id;
  return id != null ? String(id) : `slide-${index}`;
}

function getTrackY(index: number, step: number) {
  if (index <= 0) return 0;
  return PEEK_SIZE - index * step;
}

export function ScreenSlider<T>(props: Props<T>) {
  const {
    items,
    page,
    total,
    scroller,
    className = '',
    active = false,
    busy = false,
    destroyOnReachingTop = false,
    slide: Slide,
    noResults: NoResults,
    onPageChange,
    onDeactivate,
  } = props;

  const viewportRef = React.useRef<HTMLDivElement>(null);
  const trackRef = React.useRef<HTMLDivElement>(null);
  const slideStepRef = React.useRef(FALLBACK_SLIDE_STEP);
  const [slideStep, setSlideStep] = React.useState(FALLBACK_SLIDE_STEP);
  const isTransitioningRef = React.useRef(false);
  const hasMountedRef = React.useRef(false);
  const activeIndex = page - 1;

  const measureSlideStep = React.useCallback(() => {
    const track = trackRef.current;
    if (!track) return FALLBACK_SLIDE_STEP;

    const currentFrame = track.querySelector('[data-slide-frame="current"]');
    if (!(currentFrame instanceof HTMLElement)) return FALLBACK_SLIDE_STEP;

    const style = getComputedStyle(currentFrame);
    const marginBottom = parseFloat(style.marginBottom) || 0;

    return currentFrame.offsetHeight + marginBottom;
  }, []);

  const syncTrackPosition = React.useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;

    isTransitioningRef.current = true;
    gsap.to(track, {
      y: getTrackY(index, slideStepRef.current),
      duration: TRANSITION_DURATION,
      ease: 'power2.out',
      overwrite: true,
      onComplete: () => {
        isTransitioningRef.current = false;
      },
    });
  }, []);

  React.useLayoutEffect(() => {
    const step = measureSlideStep();
    slideStepRef.current = step;
    setSlideStep(step);
  }, [measureSlideStep, total, busy]);

  React.useLayoutEffect(() => {
    if (!trackRef.current) return;
    gsap.set(trackRef.current, { y: getTrackY(activeIndex, slideStepRef.current), clearProps: 'transition' });
  }, []);

  React.useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    syncTrackPosition(activeIndex);
  }, [activeIndex, syncTrackPosition]);

  React.useLayoutEffect(() => {
    if (!trackRef.current || isTransitioningRef.current) return;
    gsap.set(trackRef.current, { y: getTrackY(activeIndex, slideStepRef.current), clearProps: 'transition' });
  }, [total, busy]);

  // Swipe handlers:
  React.useEffect(() => {
    if (!active || !trackRef.current) return;

    let scrolledDistance = 0;
    let touchStartY = 0;
    let touchEndY = 0;

    scroller.subscribe('wheelSwipe', onWheelSwipeEvent);
    scroller.subscribe('touchScroll', onScrollEvent);
    scroller.subscribe('touch', onTouchEvent);

    function onWheelSwipeEvent(direction: 'up' | 'down') {
      if (isTransitioningRef.current) return;

      if (direction === 'down') {
        nextSlide();
      } else if (page > 1) {
        prevSlide();
      } else {
        if (destroyOnReachingTop) destroySlider();
        onDeactivate();
      }
    }

    function onScrollEvent(deltaY: number) {
      if (isTransitioningRef.current) return;

      scrolledDistance += deltaY;
      const baseY = getTrackY(page - 1, slideStepRef.current);
      gsap.set(trackRef.current, { y: baseY - scrolledDistance, clearProps: 'transition' });
    }

    function onTouchEvent(e: TouchEvent) {
      if (isTransitioningRef.current && e.type !== 'touchstart') return;

      switch (e.type) {
        case 'touchstart':
          scrolledDistance = 0;
          touchStartY = e.touches[0].clientY;
          touchEndY = e.touches[0].clientY;
          break;

        case 'touchmove':
          touchEndY = e.touches[0].clientY;
          break;

        case 'touchend': {
          const minDistance = 60;
          const absDistance = Math.abs(scrolledDistance);

          if (touchEndY > touchStartY && absDistance > 1.75 * minDistance) {
            prevSlide();
            return;
          }

          if (touchEndY < touchStartY && absDistance > minDistance) {
            nextSlide();
            return;
          }

          gsap.to(trackRef.current, {
            y: getTrackY(page - 1, slideStepRef.current),
            duration: TRANSITION_DURATION,
            ease: 'power2.out',
            overwrite: true,
          });
          break;
        }
      }
    }

    function prevSlide() {
      if (page === 1) {
        if (destroyOnReachingTop) destroySlider();
        onDeactivate();

        gsap.to(trackRef.current, {
          y: getTrackY(0, slideStepRef.current),
          duration: TRANSITION_DURATION,
          ease: 'power2.out',
          overwrite: true,
        });
        return;
      }

      onPageChange(page - 1);
    }

    function nextSlide() {
      if (page === total) {
        gsap.to(trackRef.current, {
          y: getTrackY(page - 1, slideStepRef.current),
          duration: TRANSITION_DURATION,
          ease: 'elastic.out(1, 0.75)',
          overwrite: true,
        });
        return;
      }

      onPageChange(page + 1);
    }

    function destroySlider() {
      scroller.unsubscribe('wheelSwipe', onWheelSwipeEvent);
      scroller.unsubscribe('touchScroll', onScrollEvent);
      scroller.unsubscribe('touch', onTouchEvent);
    }

    return destroySlider;
  }, [active, page, total, scroller, destroyOnReachingTop, onPageChange, onDeactivate]);

  const trackHeight = Math.max(total, 1) * slideStep;

  const windowSlides = WINDOW_OFFSETS.map(offset => {
    const itemIndex = activeIndex + offset;
    if (itemIndex < 0 || itemIndex >= total) return null;

    const item = items[itemIndex];
    if (!item) return null;

    return {
      offset,
      itemIndex,
      item,
      key: getSlideKey(item, itemIndex),
    };
  }).filter(Boolean) as Array<{
    offset: (typeof WINDOW_OFFSETS)[number];
    itemIndex: number;
    item: T;
    key: string;
  }>;

  return (
    <Div100vh
      className={cn(css.screenSlider, className)}
      ref={mergeRefs(props.containerRef, viewportRef)}
      style={{ ...props.style }}
    >
      {busy === false && total === 0 ? (
        <NoResults />
      ) : (
        <div className={css.track} ref={trackRef} style={{ height: trackHeight }}>
          {windowSlides.map(({ offset, itemIndex, item, key }) => (
            <div
              key={key}
              className={css.slideFrame}
              data-slide-frame={offset === 0 ? 'current' : offset === -1 ? 'prev' : 'next'}
              style={{ top: itemIndex * slideStep }}
            >
              <Slide data={busy ? null : item} position="current" isActive={offset === 0} />
            </div>
          ))}
        </div>
      )}
    </Div100vh>
  );
}

export default ScreenSlider;
